/* eslint-disable react/no-unescaped-entities */
import {
    CircularProgress,
    FormHelperText,
    InputAdornment,
    Grid,
    Typography,
    Box,
    IconButton,
} from '@mui/material';
import PrimaryButton from 'components/buttons/PrimaryButton';
import AppTextField from 'components/input-fields/AppTextField';
import { useFormik } from 'formik';
import useAuth from 'hooks/useAuth';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import LoginIcon from '@mui/icons-material/Login';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import useBoolean from 'hooks/useBoolean';

const validationSchema = yup.object({
    email: yup
        .string()
        .trim()
        .email('Please enter a valid email address')
        .required('Email is required.'),
    password: yup
        .string()
        .required('Please specify your password')
        .min(6, 'The password should have at minimum length of 6'),
});

const Form = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();
    const showPassword = useBoolean();

    const initialValues = {
        email: '',
        password: '',
    };

    const onSubmit = async (values) => {
        setLoading(true);
        await login(values.email, values.password)
            .then((res) => {
                setLoading(false);
                toast.success('You Logged In Successfully');
                if (res.user.role === 'member') {
                    navigate('/dashboard');
                }

                if (res.user.role === 'group') {
                    navigate('/dashboard/group-registration');
                }

                if (res.user.role === 'operator' || res.user.role === 'admin') {
                    navigate('/dashboard/operator');
                }

                if (res.user.role === 'judge') {
                    navigate('/dashboard/judge/abstract-to-score');
                }

                if (res.user.role === 'registrator') {
                    navigate('/dashboard/operator/registration');
                }
            })
            .catch((error) => {
                setError(error.message);
                setLoading(false);
            });
    };

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit,
    });

    return (
        <Box>
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Enter your email
                        </Typography>
                        <AppTextField
                            label="Email *"
                            variant="outlined"
                            name={'email'}
                            fullWidth
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.email &&
                                Boolean(formik.errors.email)
                            }
                            helperText={
                                formik.touched.email && formik.errors.email
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box
                            display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'stretched', sm: 'center' }}
                            justifyContent={'space-between'}
                            width={1}
                            marginBottom={2}
                        >
                            <Box marginBottom={{ xs: 1, sm: 0 }}>
                                <Typography variant={'subtitle2'}>
                                    Enter your password
                                </Typography>
                            </Box>
                            <Typography variant={'subtitle2'}>
                                <Link
                                    color={'primary'}
                                    to={'/forget-password'}
                                    style={{ textDecoration: 'none' }}
                                >
                                    Forgot your password?
                                </Link>
                            </Typography>
                        </Box>

                        <AppTextField
                            label="Password *"
                            variant="outlined"
                            name={'password'}
                            type={showPassword.value ? 'text' : 'password'}
                            fullWidth
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.password &&
                                Boolean(formik.errors.password)
                            }
                            helperText={
                                formik.touched.password &&
                                formik.errors.password
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={
                                                showPassword.value
                                                    ? 'hide the password'
                                                    : 'display the password'
                                            }
                                            onClick={() =>
                                                showPassword.onToggle()
                                            }
                                            edge="end"
                                        >
                                            {showPassword.value ? (
                                                <VisibilityOff />
                                            ) : (
                                                <Visibility />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {error && (
                        <FormHelperText
                            error
                            sx={{
                                mt: 2,
                                ml: 4,
                                fontSize: 13,
                                fontWeight: 500,
                                textAlign: 'center',
                            }}
                        >
                            {error}
                        </FormHelperText>
                    )}

                    <Grid item container xs={12}>
                        <Box
                            display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'stretched', sm: 'center' }}
                            justifyContent={'space-between'}
                            width={1}
                            maxWidth={600}
                            margin={'0 auto'}
                        >
                            <Box marginBottom={{ xs: 1, sm: 0 }}>
                                <Typography variant={'subtitle2'}>
                                    Don't have an account yet? &nbsp;
                                    <Link
                                        color={'primary'}
                                        to={'/signup'}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        Sign up here.
                                    </Link>
                                </Typography>
                            </Box>

                            <PrimaryButton
                                size={'large'}
                                variant={'contained'}
                                type={'submit'}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        Loading . . .
                                        <CircularProgress size={20} />
                                    </>
                                ) : (
                                    <>
                                        <LoginIcon /> &nbsp; Sign In
                                    </>
                                )}
                            </PrimaryButton>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default Form;
